import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { Drawer, Input, MultiSelectSearchTextField } from "components";
import {
  useAddCustomerUserGroup,
  useGetAllCustomerUsers,
  useUpdateCustomerUserGroup,
} from "controller";
import { useGetDoesHaveCustomerFullAccess } from "pages/modules/customer/context";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { getFullName } from "utilz/helpers";
import theme from "utilz/theme/theme";
import * as yup from "yup";
import { GroupMembersCustomChip } from "./GroupMembersCustomChip";
import { GroupMembersCustomTile } from "./GroupMembersCustomTile";

const schema = yup.object({
  group_name: yup.string().trim().required("Group name is required"),
  group_description: yup
    .string()
    .trim()
    .required("Group description is required")
    .max(500, "Group description must be at most 500 characters"),
  group_members: yup
    .array()
    .of(yup.string())
    .min(1, "At least one member is required"),
  // group_owner: yup.string().required("Group owner is required"),
});

function UserGroupAddDrawer({
  open,
  onClose,
  drawerData,
}: {
  open: boolean;
  onClose: () => void;
  drawerData?: any;
}) {
  const haveFullAccess = useGetDoesHaveCustomerFullAccess();
  const { id: customerIdParams }: any = useParams();
  const { mutateAsync: addUserGroups, isLoading: isAddUserGroupsLoading } =
    useAddCustomerUserGroup();
  const {
    mutateAsync: updateUserGroups,
    isLoading: isUpdateUserGroupsLoading,
  } = useUpdateCustomerUserGroup();
  const [groupMembers, setGroupMembers] = useState<any>([]); // this is for the menu

  const { data: userData } = useGetAllCustomerUsers({
    sortColumn: "first_name",
    sortDirection: "asc",
    customerId: customerIdParams,
  });

  const initialGroupMemberIds: any = []; // this is for the form
  const initialGroupMembersInMenu = drawerData?.data?.users.map((item: any) => {
    // this is for the menu
    initialGroupMemberIds.push(item?.mapping_id);
    return {
      key: `customer-${item?.mapping_id}-key`,
      label: item?.first_name + " " + item?.last_name,
      profile: item.profile_image,
      subLabel: undefined,
      value: item?.mapping_id,
    };
  });
  const defaultValues = {
    group_name: drawerData?.data?.group_name || "",
    group_description: drawerData?.data?.group_description || "",
    group_members: initialGroupMemberIds || [],
  };
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
    trigger,
  } = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (drawerData?.mode == "edit" && drawerData?.data) {
      setGroupMembers(initialGroupMembersInMenu);
      // setValue("group_owner", drawerData?.data?.group_owner_id);
      reset(defaultValues);
    }
    if (!drawerData?.open) {
      setGroupMembers([]);
      reset({
        group_name: "",
        group_description: "",
        group_members: [],
      });
    }
  }, [drawerData?.open]);

  const handleUserGroupCreate = handleSubmit(async (data: any) => {
    try {
      if (drawerData?.mode == "edit" && drawerData?.data) {
        const groupId = drawerData?.data?.mapping_id;
        // updateCustomerVendor({ vendorId, data });
        const update = await updateUserGroups({
          groupId,
          customerId: customerIdParams,
          data: { ...data, group_owner: data?.group_members?.[0] },
        });
        onClose();
        reset();
        if (update?.mapping_id) toast.success("Group updated successfully");
      } else if (drawerData?.mode == "create") {
        // addCustomerVendor({ customerIdParams, data });
        const create = await addUserGroups({
          customerIdParams,
          data: { ...data, group_owner: data?.group_members?.[0] },
        });
        onClose();
        reset();
        if (create?.mapping_id) {
          toast.success("Group created successfully");
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.message || "Something went wrong. Try again later";
      toast.error(errorMessage);
    }
  });

  const dataChangeHandler = (data: { field: any; value: any }) => {
    setGroupMembers(data.value);
    const customersMappingId = data.value.map((item: any) => item.value);
    setValue(data.field, customersMappingId, { shouldDirty: true });
    trigger(data.field);
    if (customersMappingId?.length > 0) {
      // setValue("group_owner", customersMappingId[0]);
    }
  };

  const watchGroupMembers = watch("group_members");
  const deleteMemberHandler = (data: any) => {
    const updatedMembers = groupMembers?.filter(
      (item: any) => item?.value !== data?.value,
    );
    setGroupMembers(updatedMembers);
    const updatedFormMembers = watchGroupMembers.filter(
      (memberId: any) => memberId !== data.value,
    );
    setValue("group_members", updatedFormMembers, { shouldDirty: true });
  };
  const clearAllMembers = () => {
    setGroupMembers([]);
  };

  return (
    <Drawer
      title={
        drawerData?.mode == "edit" && drawerData?.data
          ? haveFullAccess
            ? "Edit User Group"
            : "View User Group"
          : "New User Group"
      }
      drawerStyle={{ width: "404px" }}
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={onClose}
      primaryButtonText={
        drawerData?.mode === "edit" && drawerData?.data ? "Save" : "Create"
      }
      isPrimaryButtonDisabled={!isDirty}
      isLoading={isAddUserGroupsLoading || isUpdateUserGroupsLoading}
      onPrimaryButtonClick={handleUserGroupCreate}
      footer={haveFullAccess ? null : <></>}
    >
      <Box sx={{ padding: "26px" }}>
        <Stack sx={{ marginTop: "0px", width: "100%", gap: "20px" }}>
          <Input
            name={"group_name"}
            label="Name"
            required={true}
            control={control}
            disabled={!haveFullAccess}
            error={errors?.group_name?.message ? true : false}
            // @ts-ignore
            errorMessage={errors?.group_name?.message}
          ></Input>
          <Box sx={{ height: "100%", width: "100%" }}>
            <Input
              multiline={true}
              required={true}
              // label="Description.."
              name="group_description"
              rows={5}
              disabled={!haveFullAccess}
              hideHelperBox={true}
              control={control}
              sx={{
                "& .MuiInputBase-root": {
                  flex: "100%",
                  alignItems: "start",
                  backgroundColor: theme.palette.neutral["005"],
                },
                width: "100%",
              }}
              placeholder="Description..*"
              error={errors?.group_description?.message ? true : false}
              // @ts-ignore
              errorMessage={errors?.group_description?.message}
            />
          </Box>
          <Stack gap={"18px"}>
            <Typography variant="subtitle1">
              {haveFullAccess ? "Add Members" : "Members"}
            </Typography>
            {haveFullAccess && (
              <Box>
                <MultiSelectSearchTextField
                  name="group_members"
                  customPlaceholder="Search members*"
                  disabled={!haveFullAccess}
                  options={
                    Array.isArray(userData?.result)
                      ? userData.result.map((item: any) => ({
                          key: `customer-${item?.mapping_id}-key`,
                          value: item?.mapping_id,
                          profile: item?.profile_image,
                          subLabel: item?.resource_work_roles,
                          label:
                            getFullName(
                              item?.first_name,
                              item?.middle_name,
                              item?.last_name,
                            ) || "",
                        }))
                      : []
                  }
                  selectedOptions={groupMembers || []}
                  cancelLabel={"Clear all"}
                  submitLabel={"Add members"}
                  onAddSelection={dataChangeHandler}
                  onClear={clearAllMembers}
                  renderOption={(
                    props: any,
                    option: any,
                    { selected = false }: { selected: boolean },
                  ) => (
                    <li
                      {...props}
                      style={{
                        borderBottom: "1px solid #BFC4CD99",
                        backgroundColor: "white",
                      }}
                    >
                      <GroupMembersCustomTile
                        data={option}
                        isSelected={selected}
                      />
                    </li>
                  )}
                />

                <Typography
                  variant="body-small"
                  sx={{
                    paddingLeft: "14px",
                    color: "#CE293A",
                  }}
                  color={theme.palette.error.main}
                >
                  {/* @ts-ignore */}
                  {errors?.group_members?.message}
                </Typography>
              </Box>
            )}
            <Grid
              container
              {...{ lg: 12 }}
              spacing={"16px"}
              sx={{
                marginBottom: "16px",
              }}
            >
              {groupMembers
                ? groupMembers.map((item: any, index: number) => (
                    <Grid
                      key={`group_member_index_${index}`}
                      item
                      {...{ xs: 6, sm: 4, md: 4, lg: 4, xl: 4 }}
                      sx={{ marginRight: "4px" }}
                    >
                      <GroupMembersCustomChip
                        data={item}
                        onDelete={deleteMemberHandler}
                      />
                    </Grid>
                  ))
                : null}
            </Grid>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}

export default UserGroupAddDrawer;
